import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import YearSelect from 'Components/yearSelect';
import Tooltip from 'Components/tooltip';
import Toolbox from 'Components/toolbox';
import { push } from 'Actions/router';
import qs from 'qs';
import * as buttonStyles from '../../styles/buttons.css';
import * as iconStyles from '../../styles/icons.css';
import * as styles from './tableCellFullWidth.css';

class TableCellFullWidth extends Component {
  constructor() {
    super();

    this.state = { toolboxVisible: false };
    this.showToolbox = this.showToolbox.bind(this);
    this.renderLink = this.renderLink.bind(this);
    this.renderNotes = this.renderNotes.bind(this);
    this.renderToolbox = this.renderToolbox.bind(this);
    this.renderYear = this.renderYear.bind(this);
  }

  showToolbox() {
    this.setState((prevState) => ({
      toolboxVisible: !prevState.toolboxVisible,
    }));
  }

  renderLink() {
    const { data: { hasMega = false } } = this.props;

    return this.props.data.url && (
      <a
        className={hasMega ? styles.url : styles.urlDark}
        href={this.props.data.url}
        target="_blank"
        rel="noopener noreferrer"
      >
        <i className={iconStyles.iconLink} />
        <span>Open Full Indicator</span>
      </a>
    );
  }

  renderNotes() {
    const { data: { hasMega = false, name, active = {}, notes } } = this.props;
    const btnProps = {
      className: hasMega ? styles.notes : styles.notesDark,
      content: (
        <span className={styles.noteWrapper}>
          <i className={iconStyles.iconInfo} />
          <span>Notes</span>
        </span>
      ),
    };

    if ('undefined' === typeof notes) {
      return null;
    }

    /**
     * Build notes object. Needs to be in the shape of an object
     * with keys designating each subsection (notes/sources/defs)
     * and values being arrays of strings to be displayed in the
     * tooltip.
     */
    const notesKeys = Object.keys(notes);
    const mappedNotes = notesKeys
      .reduce((acc, item) => ({
        ...acc,
        [item]: notes[item].filter(({ key: limits }) => (
          // Filter out notes/sources/defs for non-active years
          'undefined' === typeof limits ||
            'undefined' === typeof limits.timeframe ||
            active.year === limits.timeframe
        ))
          .map(({ value: text }) => text)
          .reduce((acum, curr) => acum.concat(curr), []),
      }), {});

    return this.props.data.notes && (
      <Tooltip
        btnProps={btnProps}
        content={mappedNotes}
        title={`${name}${active.year ? `, ${active.year}` : ''}`}
      />
    );
  }

  renderToolbox() {
    const {
      id,
      hasMega = false,
      facetCount,
      characteristicCount,
      children = [],
    } = this.props.data;
    const noFacetsOrCharacteristics = 0 === (facetCount + characteristicCount);
    const hasChildren = 0 !== children.length;

    if (hasMega || (noFacetsOrCharacteristics && !hasChildren)) {
      return null;
    }

    return (
      <div className={styles.controlsRow}>
        <button className={styles.refine} onClick={this.showToolbox}>
          Refine Data
        </button>
        {this.state.toolboxVisible &&
          <Toolbox
            id={id}
            onClose={this.showToolbox}
            {...this.props.data}
          />}
      </div>
    );
  }

  renderYear() {
    const {
      id,
      fullId,
      years,
      active: { year },
      children = false,
      hasMega = false,
    } = this.props.data;
    const isMegaTitle = children || !hasMega;
    const className = [
      styles.year,
      1 < years.length ? styles.yearHasDropdown : '',
      isMegaTitle && 1 < years.length ? styles.megaYearHasDropdown : '',
    ].join(' ');
    return (
      <span className={className}>,&nbsp;
        {1 < years.length ?
          <YearSelect
            years={years}
            active={year}
            id={id}
            fullId={fullId}
            isMegaTitle={isMegaTitle}
          /> :
          year}
      </span>
    );
  }

  render() {
    const {
      data: { name: title, active, hasMega = false, children = false },
      onRemove,
    } = this.props;

    const className = [
      styles.headerRow,
      children || !hasMega ? styles.megaTitle : '',
    ].join(' ');

    return (
      <div>
        <div className={className}>
          <span className={styles.header}>
            {title}
            {!!active && !!active.year && this.renderYear()}
          </span>
          {this.renderNotes()}
          {this.renderLink()}
          <button
            className={[
              buttonStyles.closeDark,
              (children || !hasMega) ? styles.removeLight : styles.removeDark,
            ].join(' ')}
            onClick={onRemove}
          >
            <i className={iconStyles.iconClose} />
          </button>
        </div>
        {this.renderToolbox()}
      </div>
    );
  }
}

// @TODO transform this into a proper typecheck
TableCellFullWidth.propTypes = {
  data: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch, { ...props }) => {
  const { store } = props;
  const { getState } = store;
  // If we're removing our states, reset app, but retain indicators.
  if (getState().router && 1 === getState().router.indicators.length) {
    return {
      onRemove: () => {
        const params = {
          stage: 1,
        };
        const {
          geos,
        } = getState().router;
        // Ensure that geos are always an array.
        const geoConverted = 'string' === typeof geos ? [geos] : geos;
        if (geoConverted) {
          params.regions = geoConverted.map((geo) => geo.toUpperCase()).join(',');
        }
        window.location.href = `/statedata/custom/?${qs.stringify(params)}`; // phpcs:ignore WordPressVIPMinimum.JS.Window.location
      },
    };
  }

  return {
    onRemove: (id) => dispatch(push({ indicators: id })),
  };
};

const mergeProps = (stateProps, { onRemove }, ownProps) => ({
  ...ownProps,
  onRemove: ownProps.data.children ?
    () => ownProps.data.children
      .map(({ fullId }) => fullId)
      .forEach(onRemove) :
    () => onRemove(ownProps.data.fullId),
});

const Connected = connect(
  undefined,
  mapDispatchToProps,
  mergeProps
)(TableCellFullWidth);

export default (props) =>
  <Connected {...props} store={props.context.store()} />; // eslint-disable-line react/prop-types

export {
  TableCellFullWidth,
  mapDispatchToProps,
  mergeProps,
};



// WEBPACK FOOTER //
// ./app/components/tableCellFullWidth/index.js